/* eslint-disable no-unused-expressions */
import { useLocation } from "@gatsbyjs/reach-router";
import { Link } from "gatsby";
import React, { useCallback, useContext } from "react";
import { Flex } from "rebass/styled-components";

import { layout } from "../constants/constants";
import CondensedNavDesktop from "../navigation/condensed-nav-desktop/condensed-nav-desktop";
import GlobalNavMobile from "../navigation/global-nav-mobile/global-nav-mobile";
import MobileNavOverlay from "../navigation/mobile-nav-overlay/mobile-nav-overlay";
import ShIcon from "../shared/components/sh-icon/sh-icon";
import { Media } from "../shared/contexts/media/MediaContext";
import { UiStateContext } from "../shared/contexts/ui-state/UiStateContext";
import useFeatureToggle from "../shared/hooks/use-feature-toggle/useFeatureToggle";
import { FeatureToggle, GlobalNavContent, IconPack, PrimaryHeaderContent, GlobalToolbarContent } from "../shared/models/app-data-model";
import "./condensed-primary-header.scss";

export type CondensedPrimaryHeaderProps = {
  headerContent: PrimaryHeaderContent;
  navContent: GlobalNavContent;
  title: string;
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
  campaignPageActive?: boolean;
  corewellStyleOverrides?: boolean;
  globalToolbarContent?: GlobalToolbarContent;
};

const CondensedPrimaryHeader: React.FC<CondensedPrimaryHeaderProps> = ({
  campaignPageActive,
  featureToggles,
  headerContent,
  iconPack,
  navContent,
  title,
  globalToolbarContent,
  corewellStyleOverrides
}: CondensedPrimaryHeaderProps) => {
  const uiStateContext = useContext(UiStateContext);
  const showSearch = useFeatureToggle("search", featureToggles);
  const globalSearchPageEnalbed = useFeatureToggle("global-search-page", featureToggles);
  const showMobileNavOverlay = useFeatureToggle("mobile-nav-overlay", featureToggles);
  const megaMenuEnabled = useFeatureToggle("Corewell-health-mega-menu", featureToggles);
  const location = useLocation();

  const closeMenus = useCallback((): void => {
    uiStateContext.setMegaMenuOpen?.(false);
    uiStateContext.setMenuOpen?.(false);
  }, [uiStateContext.setMegaMenuOpen, uiStateContext.setMenuOpen]);

  const handleMenuClick = useCallback((): void => {
    if (showMobileNavOverlay) {
      uiStateContext.setScrollPosition?.(window.scrollY);
      uiStateContext.setMenuOpen?.(true);

      if (megaMenuEnabled) {
        uiStateContext.setMegaMenuOpen?.(true);
      }
    } else {
      uiStateContext.setMenuOpen?.(!uiStateContext.menuOpen);
      uiStateContext.setMegaMenuOpen?.(!uiStateContext.megaMenuOpen);
    }
  }, [uiStateContext.menuOpen, uiStateContext.setMenuOpen, uiStateContext.megaMenuOpen, uiStateContext.setMegaMenuOpen]);

  const handleCloseButton = useCallback((): void => {
    uiStateContext.setMenuOpen?.(false);
    uiStateContext.setMegaMenuOpen?.(false);

    setTimeout(() => {
      window.scrollTo({
        top: uiStateContext.scrollPosition,
        behavior: "smooth"
      });
    }, 1);
  }, [uiStateContext.setMenuOpen, uiStateContext.setMegaMenuOpen, uiStateContext.scrollPosition]);

  const handleSearchClick = useCallback((): void => {
    uiStateContext.setSearchOpen?.(true);
  }, [uiStateContext.setSearchOpen]);

  const stackedLogo = (
    <div className="left-content">
      {headerContent.mobile_logo?.url && (
        <Link to={"/"} onClick={() => closeMenus()} className="logo-area" data-testid="corewell-logo">
          <img src={headerContent.mobile_logo?.url} className={`logo`} alt={`${title} Logo`} />
        </Link>
      )}
    </div>
  );

  const horizontalLogo = (
    <div className="left-content">
      {headerContent.logo?.url && (
        <Link to={"/"} onClick={() => closeMenus()} className="logo-area" data-testid="corewell-logo">
          <img src={headerContent.logo?.url} className={`logo`} alt={`${title} Logo`} />
        </Link>
      )}
    </div>
  );

  const renderLogo = (
    <>
      <Media lessThan="tablet">{stackedLogo}</Media>
      <Media at="tablet">{horizontalLogo}</Media>
      <Media at="desktop">{stackedLogo}</Media>
      <Media greaterThanOrEqual="xl">{horizontalLogo}</Media>
    </>
  );

  const mobileNav =
    uiStateContext?.menuOpen &&
    !campaignPageActive &&
    (showMobileNavOverlay ? (
      <MobileNavOverlay
        iconPack={iconPack}
        content={navContent}
        featureToggles={featureToggles}
        globalToolbarContent={globalToolbarContent}
      />
    ) : (
      <GlobalNavMobile iconPack={iconPack} content={navContent} featureToggles={featureToggles} />
    ));

  const searchBtnClasses = `global-nav-btn global-nav-search-btn unstyled-btn ${
    uiStateContext?.menuOpen && uiStateContext?.megaMenuOpen ? "circle-icon-btn" : ""
  }`;

  return (
    <div className="condensed-primary-header-container" id="condensed-header-container">
      <Flex className="condensed-primary-header">
        <Flex className="primary-header-content" width={layout.pageWidth} px={layout.pagePaddingX}>
          {renderLogo}
          {!campaignPageActive && (
            <div className="right-content">
              <Media greaterThanOrEqual="desktop">
                {!campaignPageActive && <CondensedNavDesktop iconPack={iconPack} content={navContent} featureToggles={featureToggles} />}
              </Media>
              {showSearch && !globalSearchPageEnalbed && (
                <button className={searchBtnClasses} data-testid="search-icon" onClick={handleSearchClick} aria-label="Search">
                  <ShIcon iconPack={iconPack} iconName="search" className="icon search-icon" />
                </button>
              )}
              {showSearch && globalSearchPageEnalbed && (
                <Link
                  to={"/search"}
                  onClick={location.pathname === "/search" ? () => closeMenus() : undefined}
                  className={searchBtnClasses}
                  aria-label="Search"
                  data-testid="search-icon"
                >
                  <ShIcon iconPack={iconPack} iconName="search" className="icon search-icon" />
                </Link>
              )}
              <Media lessThan="desktop">
                {megaMenuEnabled && uiStateContext?.megaMenuOpen && uiStateContext?.menuOpen ? (
                  <button className="unstyled-btn close-btn" data-testid="close-btn" onClick={() => handleCloseButton()}>
                    <ShIcon iconPack={iconPack} className="icon close-icon" iconName="close" />
                  </button>
                ) : (
                  <button className="global-nav-btn unstyled-btn" data-testid="menu-btn" onClick={() => handleMenuClick()}>
                    {showMobileNavOverlay ? "Menu" : uiStateContext?.menuOpen ? "Close" : "Menu"}
                  </button>
                )}
              </Media>
            </div>
          )}
        </Flex>
      </Flex>
      {mobileNav}
    </div>
  );
};

export default CondensedPrimaryHeader;
